import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Single source of truth for "assignment completion".
 *
 * Backed by public.get_team_assignment_completion(team_id), which reuses the
 * existing lesson-completion rules and standalone media completion thresholds.
 * Partial video watch progress never counts.
 */
export type AssignmentCompletion = {
  team_id: string;
  total_active_assignments: number;
  player_count: number;
  total_assignment_instances: number;
  completed_assignment_instances: number;
  completion_pct: number;
  has_data: boolean;
  players: { user_id: string; username: string; assigned: number; completed: number; pct: number }[];
  units: {
    unit_id: string;
    unit_type: string;
    source_id: string;
    title: string;
    total: number;
    done: number;
    pct: number;
  }[];
};

const EMPTY: AssignmentCompletion = {
  team_id: '',
  total_active_assignments: 0,
  player_count: 0,
  total_assignment_instances: 0,
  completed_assignment_instances: 0,
  completion_pct: 0,
  has_data: false,
  players: [],
  units: [],
};


export function useAssignmentCompletion(teamId: string | undefined) {
  const [data, setData] = useState<AssignmentCompletion>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!teamId) { setData(EMPTY); setError(null); setLoading(false); return; }
    const { data: res, error } = await supabase.rpc('get_team_assignment_completion' as any, {
      p_team_id: teamId,
    });
    if (!error && res) {
      setData(res as unknown as AssignmentCompletion);
      setError(null);
    } else {
      setData({ ...EMPTY, team_id: teamId });
      setError(error?.message ?? 'Assignment completion is unavailable');
    }
    setLoading(false);
  }, [teamId]);

  useEffect(() => {
    setLoading(true);
    refresh();
    const handleChange = () => refresh();
    window.addEventListener('assignment-completion-changed', handleChange);
    return () => window.removeEventListener('assignment-completion-changed', handleChange);
  }, [refresh]);

  return { completion: data, loading, error, refresh };
}
